import React, { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import ReactModal from 'react-modal';
import { useToasts } from 'react-toast-notifications';
import Alert from '../../../components/Alert';
import Button from '../../../components/Button';
import Error from '../../../components/Error';
import Modal, { MODAL_STYLES } from '../../../components/Modal';
import { useAxios } from '../../../utils/hooks';
import VALIDATORS from '../../../utils/validators';
import c from 'classnames';

interface VerifyDomainResponse {
    id: number;
    user_id: number;
    subdomain: string;
    domain: string;
    username: string;
    ips: any[];
    custom_spf: boolean;
    default: boolean;
    legacy: boolean;
    automatic_security: boolean;
    valid: boolean;
    dns: DNS;
}

interface DNS {
    mail_cname: DnsSetting;
    dkim1: DnsSetting;
    dkim2: DnsSetting;
}

interface DnsSetting {
    valid: boolean;
    type: string;
    host: string;
    data: string;
}

interface VerifiedSenderRequest {
    nickname: string;
    email: string;
    address: {
        address: string;
        state: string;
        city: string;
        zip: string;
        country: string;
    };
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSenderVerified: (email: string) => void;
}

export default function VerifySenderModal({ isOpen, onClose, onSenderVerified }: Props) {
    const handleSenderVerified = (email: string) => {
        onSenderVerified(email);
        onClose();
    };
    return (
        <ReactModal isOpen={isOpen} style={MODAL_STYLES}>
            <Modal
                type="INFO"
                title="Verify an Email Sender"
                body={<SenderForm onSenderVerified={handleSenderVerified} />}
                confirmButtonText="Close"
                onConfirm={onClose}
            />
        </ReactModal>
    );
}

function SenderForm(props: { onSenderVerified: (email: string) => void }) {
    const { addToast } = useToasts();
    const axios = useAxios();
    const [isSaving, setIsSaving] = useState(false);
    const [domainVerification, setDomainVerification] = useState<VerifyDomainResponse>();
    const handleDomainVerified = () => setDomainVerification(undefined);
    const verifySender = useCallback(
        (request: VerifiedSenderRequest) => {
            if (!axios || !axios.current) {
                return;
            }

            setIsSaving(true);
            return axios.current
                .post('/email-sender/verify', request)
                .then((res) => {
                    setIsSaving(false);

                    if (res.status === 202) {
                        //Domain needs to be verified
                        setDomainVerification(res.data as VerifyDomainResponse);
                    } else {
                        props.onSenderVerified(request.email);
                    }

                    return res;
                })
                .catch((err) => {
                    setIsSaving(false);
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                });
        },
        [axios, addToast, props]
    );

    return (
        <Form<VerifiedSenderRequest> onSubmit={verifySender}>
            {({ handleSubmit, invalid }) => (
                <>
                    <p>
                        Enter an email address that you own. You will be sent an email containing a code in order to
                        verify you own that address.
                    </p>
                    {domainVerification && (
                        <>
                            <Alert
                                title="Domain Verification"
                                colour="YELLOW"
                                body="Before you can verify this sender, please verify you own the sender domain."
                            />
                            <p className="mt-2">
                                Please add the following entries to your DNS for{' '}
                                <span className="font-bold">{domainVerification.domain}</span>, then click "Verify"
                                below.
                            </p>
                            <DnsVerificationTable data={domainVerification} onDomainVerified={handleDomainVerified} />
                        </>
                    )}
                    <div className={c({ hidden: !!domainVerification })}>
                        <div className="mb-2">
                            <label className="text-sm text-gray-600">Name</label>
                            <Field name="nickname" type="text" validate={VALIDATORS.required}>
                                {(props) => (
                                    <>
                                        <input {...props.input} className="input w-full" placeholder="Sender Name" />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="mb-2">
                            <label className="text-sm text-gray-600">Email</label>
                            <Field name="email" type="email" validate={VALIDATORS.requiredEmail}>
                                {(props) => (
                                    <>
                                        <input
                                            {...props.input}
                                            className="input w-full"
                                            placeholder="you@example.com"
                                        />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <hr className="py-4" />
                        <div className="mb-2">
                            <label className="text-sm text-gray-600">Address</label>
                            <Field name="address.address" type="text" validate={VALIDATORS.required}>
                                {(props) => (
                                    <>
                                        <input {...props.input} className="input w-full" />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="mb-2">
                            <label className="text-sm text-gray-600">City</label>
                            <Field name="address.city" type="text" validate={VALIDATORS.required}>
                                {(props) => (
                                    <>
                                        <input {...props.input} className="input w-full" />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="mb-2">
                            <label className="text-sm text-gray-600">Country</label>
                            <Field name="address.country" type="text" validate={VALIDATORS.required}>
                                {(props) => (
                                    <>
                                        <input
                                            {...props.input}
                                            className="input w-full"
                                            placeholder="Country of the sender"
                                        />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="flex flex-row-reverse">
                            <Button onClick={handleSubmit} disabled={invalid} state={isSaving ? 'SAVING' : 'READY'}>
                                Verify
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Form>
    );
}

interface DnsValidationResult {
    id: number;
    valid: boolean;
    validation_results: ValidationResults;
}

interface ValidationResults {
    mail_cname: DnsResult;
    dkim1: DnsResult;
    dkim2: DnsResult;
}

interface DnsResult {
    valid: boolean;
    reason: string;
}

const isValid = (key: keyof (DNS & ValidationResults), dns: DNS, dnsResult?: ValidationResults) => {
    return dnsResult ? dnsResult[key].valid : dns[key].valid;
};

function DnsVerificationTable(props: { data: VerifyDomainResponse; onDomainVerified: () => void }) {
    const axios = useAxios();
    const { addToast } = useToasts();
    const [submitting, setSubmitting] = useState(false);
    const [verifyResponse, setVerifyResponse] = useState<DnsValidationResult>();
    const handleVerify = useCallback(() => {
        if (!axios || !axios.current) {
            return;
        }
        setSubmitting(true);
        axios.current
            .post<DnsValidationResult>('/email-sender/check-domain-authentication', { id: props.data.id })
            .then((res) => {
                setSubmitting(false);
                setVerifyResponse(undefined);
                setVerifyResponse(res.data);
                if (res.data.valid) {
                    props.onDomainVerified();
                }
            })
            .catch((err) => {
                setSubmitting(false);
                addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
            });
    }, [axios, props, addToast]);
    const { dns } = props.data;
    return (
        <>
            <table className="min-w-max w-full table-auto my-4">
                <thead>
                    <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                        <th className="py-3 px-6 text-left">Status</th>
                        <th className="py-3 px-6 text-left">Type</th>
                        <th className="py-3 px-6 text-left">Host</th>
                        <th className="py-3 px-6 text-left">Value</th>
                    </tr>
                </thead>
                <tbody className="text-gray-600 text-sm font-light">
                    <tr className="border-b border-gray-200 hover:bg-gray-100">
                        <td className="py-3 px-6 text-left whitespace-nowrap">
                            <div className="flex items-center">
                                <span className="font-medium text-base">
                                    <Alert
                                        title={
                                            isValid('mail_cname', dns, verifyResponse?.validation_results)
                                                ? 'Valid'
                                                : 'Pending'
                                        }
                                        colour={
                                            isValid('mail_cname', dns, verifyResponse?.validation_results)
                                                ? 'GREEN'
                                                : 'GREY'
                                        }
                                    />
                                </span>
                            </div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.mail_cname.type.toUpperCase()}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.mail_cname.host}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.mail_cname.data}</div>
                        </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-100">
                        <td className="py-3 px-6 text-left whitespace-nowrap">
                            <div className="flex items-center">
                                <span className="font-medium text-base">
                                    <Alert
                                        title={
                                            isValid('dkim1', dns, verifyResponse?.validation_results)
                                                ? 'Valid'
                                                : 'Pending'
                                        }
                                        colour={
                                            isValid('dkim1', dns, verifyResponse?.validation_results) ? 'GREEN' : 'GREY'
                                        }
                                    />
                                </span>
                            </div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim1.type.toUpperCase()}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim1.host}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim1.data}</div>
                        </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-100">
                        <td className="py-3 px-6 text-left whitespace-nowrap">
                            <div className="flex items-center">
                                <span className="font-medium text-base">
                                    <Alert
                                        title={
                                            isValid('dkim2', dns, verifyResponse?.validation_results)
                                                ? 'Valid'
                                                : 'Pending'
                                        }
                                        colour={
                                            isValid('dkim2', dns, verifyResponse?.validation_results) ? 'GREEN' : 'GREY'
                                        }
                                    />
                                </span>
                            </div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim2.type.toUpperCase()}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim2.host}</div>
                        </td>
                        <td className="py-3 px-6 text-left">
                            <div className="flex items-center">{dns.dkim2.data}</div>
                        </td>
                    </tr>
                </tbody>
            </table>
            {verifyResponse && (
                <Alert
                    title={`Verification ${verifyResponse.valid ? 'Passed' : 'Failed'}`}
                    colour={verifyResponse.valid ? 'GREEN' : 'RED'}
                />
            )}
            <div className="flex flex-row-reverse">
                <Button onClick={handleVerify} disabled={submitting} state={submitting ? 'SAVING' : 'READY'}>
                    Verify
                </Button>
            </div>
        </>
    );
}
